import { clsx } from 'clsx'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Graph } from '@entities/graph'

import {
  CHART_TYPES,
  CHART_INK,
  NUTRIENT_COLORS,
  NUTRIENT_FADED_COLORS,
  NUTRIENT_FILL_COLORS,
} from '@shared/config'
import { notyError } from '@shared/lib/notifications'
import { getDayJs } from '@shared/lib/utils'
import { Btn } from '@shared/ui/btn'

import { getTarget, getMealsForDate, getMealsForPeriod } from '../api'

import styles from './styles.module.scss'

//
//

const dayjs = getDayJs()

const DATE_FORMAT = 'YYYY-MM-DD'

const MODES = {
  TODAY: 'today',
  PERIOD: 'period',
}

const PERIOD_PRESETS = [
  { key: 'week', days: 7 },
  { key: 'twoWeeks', days: 14 },
  { key: 'month', days: 30 },
]

const MEAL_TYPES_ORDER = [
  'breakfast',
  'morning_snack',
  'lunch',
  'afternoon_snack',
  'dinner',
  'evening_snack',
  'unknown',
]

// metric key -> field in mealData.info; targets use the same keys in currentTarget
const METRIC_INFO_KEYS = {
  calories: 'totalCalories',
  proteins: 'totalProteins',
  fats: 'totalFats',
  carbs: 'totalCarbs',
}

const METRICS = Object.keys(METRIC_INFO_KEYS)

//
//

const getMealMetric = (meal, metric) =>
  Number(meal?.mealData?.info?.[METRIC_INFO_KEYS[metric]]) || 0

const getTotalMetric = (meals, metric) =>
  meals.reduce((sum, meal) => sum + getMealMetric(meal, metric), 0)

// running total of the metric through the day, one point per meal-type slot
const getCumulativeByMealType = (meals, metric) => {
  const totals = {}

  meals.forEach((meal) => {
    const type = MEAL_TYPES_ORDER.includes(meal?.possibleMealType)
      ? meal.possibleMealType
      : 'unknown'

    totals[type] = (totals[type] || 0) + getMealMetric(meal, metric)
  })

  //

  let sum = 0

  return MEAL_TYPES_ORDER.map((type) => {
    sum += totals[type] || 0

    return sum
  })
}

const getTotalsPerDay = (meals, startDate, days, metric) => {
  const totals = {}

  meals.forEach((meal) => {
    const day = dayjs(meal?.addedAt).format(DATE_FORMAT)

    totals[day] = (totals[day] || 0) + getMealMetric(meal, metric)
  })

  //

  return Array.from({ length: days }, (_, i) => {
    const day = startDate.add(i, 'day')

    return {
      label: day.format('DD.MM'),
      total: totals[day.format(DATE_FORMAT)] || 0,
    }
  })
}

const getTargetDataset = (label, targetValue, pointsCount) => ({
  label,
  data: Array.from({ length: pointsCount }, () => targetValue),
  borderColor: CHART_INK.muted,
  pointBackgroundColor: CHART_INK.muted,
  borderDash: [6, 6],
  pointRadius: 0,
  pointHoverRadius: 0,
  fill: false,
})

//
//

const FCGraphStats = () => {
  const { t } = useTranslation()

  //
  //

  const [mode, setMode] = useState(MODES.TODAY)
  const [metric, setMetric] = useState(METRICS[0])
  const [compareYesterday, setCompareYesterday] = useState(false)
  const [periodDays, setPeriodDays] = useState(PERIOD_PRESETS[0].days)

  // currentTarget object - holds the target value per metric
  const [targets, setTargets] = useState(null)

  // null = loading, { error: true } = failed, array = loaded
  const [todayMeals, setTodayMeals] = useState(null)
  const [yesterdayMeals, setYesterdayMeals] = useState(null)

  // tagged with the requested days count, so a preset change reads as loading
  const [periodMeals, setPeriodMeals] = useState(null)

  //
  //

  useEffect(() => {
    const fn = async () => {
      const [targetData, mealsData] = await Promise.all([
        getTarget(),
        getMealsForDate({ date: dayjs().format(DATE_FORMAT) }),
      ])

      if (targetData?.error) {
        notyError(t('graphStats:error.target'))
      } else {
        setTargets(targetData?.data?.currentTarget || null)
      }

      if (mealsData?.error) {
        notyError(t('graphStats:error.meals'))
        setTodayMeals({ error: true })
      } else {
        setTodayMeals(mealsData?.data || [])
      }
    }

    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //
  //

  useEffect(() => {
    if (!compareYesterday || yesterdayMeals) {
      return
    }

    const fn = async () => {
      const mealsData = await getMealsForDate({
        date: dayjs().subtract(1, 'day').format(DATE_FORMAT),
      })

      if (mealsData?.error) {
        notyError(t('graphStats:error.meals'))
        setYesterdayMeals({ error: true })
        return
      }

      setYesterdayMeals(mealsData?.data || [])
    }

    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareYesterday, yesterdayMeals])

  //
  //

  useEffect(() => {
    if (mode !== MODES.PERIOD) {
      return undefined
    }

    let cancelled = false

    const fn = async () => {
      const endDate = dayjs()
      const startDate = endDate.subtract(periodDays - 1, 'day')

      const mealsData = await getMealsForPeriod({
        startDate: startDate.format(DATE_FORMAT),
        endDate: endDate.format(DATE_FORMAT),
      })

      if (cancelled) {
        return
      }

      if (mealsData?.error) {
        notyError(t('graphStats:error.meals'))
        setPeriodMeals({ days: periodDays, error: true })
        return
      }

      setPeriodMeals({ days: periodDays, list: mealsData?.data || [] })
    }

    fn()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, periodDays])

  //
  //

  const toggleCompareYesterday = () => {
    // drop a failed fetch so the next toggle-on retries it
    if (compareYesterday && yesterdayMeals?.error) {
      setYesterdayMeals(null)
    }

    setCompareYesterday(!compareYesterday)
  }

  //
  //

  const metricUnit = t(
    `graphStats:units.${metric === 'calories' ? 'kcal' : 'g'}`
  )

  const chartData = useMemo(() => {
    const targetValue = targets?.[metric]

    if (mode === MODES.TODAY) {
      if (!todayMeals || (compareYesterday && !yesterdayMeals)) {
        return null
      }

      if (todayMeals.error || (compareYesterday && yesterdayMeals.error)) {
        return { error: true }
      }

      //

      const datasets = [
        {
          label: t('graphStats:series.today'),
          data: getCumulativeByMealType(todayMeals, metric),
          borderColor: NUTRIENT_COLORS[metric],
          pointBackgroundColor: NUTRIENT_COLORS[metric],
          backgroundColor: NUTRIENT_FILL_COLORS[metric],
          fill: compareYesterday ? false : 'origin',
        },
      ]

      if (compareYesterday) {
        datasets.push({
          label: t('graphStats:series.yesterday'),
          data: getCumulativeByMealType(yesterdayMeals, metric),
          borderColor: NUTRIENT_FADED_COLORS[metric],
          pointBackgroundColor: NUTRIENT_FADED_COLORS[metric],
          borderDash: [4, 4],
          fill: false,
        })
      }

      if (targetValue) {
        datasets.push(
          getTargetDataset(
            t('graphStats:series.target'),
            targetValue,
            MEAL_TYPES_ORDER.length
          )
        )
      }

      return {
        labels: MEAL_TYPES_ORDER.map((type) =>
          t(`graphStats:mealTypes.${type}`)
        ),
        datasets,
      }
    }

    //
    //

    if (periodMeals?.days !== periodDays) {
      return null
    }

    if (periodMeals.error) {
      return { error: true }
    }

    //

    const endDate = dayjs()
    const startDate = endDate.subtract(periodDays - 1, 'day')

    const perDay = getTotalsPerDay(
      periodMeals.list,
      startDate,
      periodDays,
      metric
    )

    const datasets = [
      {
        label: t(`graphStats:metrics.${metric}`),
        data: perDay.map((day) => day.total),
        borderColor: NUTRIENT_COLORS[metric],
        pointBackgroundColor: NUTRIENT_COLORS[metric],
        backgroundColor: NUTRIENT_FILL_COLORS[metric],
        fill: 'origin',
      },
    ]

    if (targetValue) {
      datasets.push(
        getTargetDataset(
          t('graphStats:series.target'),
          targetValue,
          perDay.length
        )
      )
    }

    return {
      labels: perDay.map((day) => day.label),
      datasets,
    }
  }, [
    mode,
    metric,
    compareYesterday,
    periodDays,
    todayMeals,
    yesterdayMeals,
    periodMeals,
    targets,
    t,
  ])

  //
  //

  const isEmpty = useMemo(() => {
    if (mode === MODES.TODAY) {
      return (
        Array.isArray(todayMeals) &&
        !todayMeals.length &&
        (!compareYesterday ||
          (Array.isArray(yesterdayMeals) && !yesterdayMeals.length))
      )
    }

    return (
      periodMeals?.days === periodDays &&
      Array.isArray(periodMeals.list) &&
      !periodMeals.list.length
    )
  }, [
    mode,
    compareYesterday,
    todayMeals,
    yesterdayMeals,
    periodMeals,
    periodDays,
  ])

  const compareTotals =
    mode === MODES.TODAY &&
    compareYesterday &&
    Array.isArray(todayMeals) &&
    Array.isArray(yesterdayMeals)
      ? {
          today: getTotalMetric(todayMeals, metric),
          yesterday: getTotalMetric(yesterdayMeals, metric),
        }
      : null

  //
  //

  return (
    <section className={styles.stats}>
      <div className={styles.stats__header}>
        <h2>{t('graphStats:title')}</h2>

        <div className={styles.stats__modes}>
          {Object.values(MODES).map((modeKey) => (
            <Btn
              key={modeKey}
              design={['wrapper']}
              className={clsx(styles.stats__mode, {
                [styles.stats__mode_active]: mode === modeKey,
              })}
              onClick={() => setMode(modeKey)}
            >
              {t(`graphStats:modes.${modeKey}`)}
            </Btn>
          ))}
        </div>
      </div>

      {/* ************************************************************************** */}

      <div className={styles.stats__metrics}>
        {METRICS.map((metricKey) => (
          <Btn
            key={metricKey}
            design={['wrapper']}
            className={clsx(styles.chip, {
              [styles.chip_active]: metric === metricKey,
            })}
            onClick={() => setMetric(metricKey)}
          >
            <i
              className={styles.chip__dot}
              style={{ backgroundColor: NUTRIENT_COLORS[metricKey] }}
            />
            {t(`graphStats:metrics.${metricKey}`)}
          </Btn>
        ))}
      </div>

      {/* ************************************************************************** */}

      <div className={styles.stats__controls}>
        {mode === MODES.TODAY ? (
          <Btn
            design={['wrapper']}
            className={clsx(styles.chip, {
              [styles.chip_active]: compareYesterday,
            })}
            onClick={toggleCompareYesterday}
          >
            <i
              className={clsx(
                'fa-solid',
                compareYesterday ? 'fa-check' : 'fa-code-compare'
              )}
            />
            {t('graphStats:compareWithYesterday')}
          </Btn>
        ) : (
          PERIOD_PRESETS.map((preset) => (
            <Btn
              key={preset.key}
              design={['wrapper']}
              className={clsx(styles.chip, {
                [styles.chip_active]: periodDays === preset.days,
              })}
              onClick={() => setPeriodDays(preset.days)}
            >
              {t(`graphStats:periods.${preset.key}`)}
            </Btn>
          ))
        )}
      </div>

      {/* ************************************************************************** */}

      {isEmpty ? (
        <p className={styles.stats__empty}>
          {t(`graphStats:empty.${mode === MODES.TODAY ? 'today' : 'period'}`)}
        </p>
      ) : (
        <Graph
          type={CHART_TYPES.LINE}
          data={chartData}
          yAxisUnit={metricUnit}
        />
      )}

      {/* ************************************************************************** */}

      {compareTotals && (
        <div className={styles.totals}>
          <span className={styles.totals__item}>
            <i style={{ backgroundColor: NUTRIENT_COLORS[metric] }} />
            {t('graphStats:series.today')}: <b>{compareTotals.today}</b>{' '}
            {metricUnit}
          </span>

          <span className={styles.totals__item}>
            <i style={{ backgroundColor: NUTRIENT_FADED_COLORS[metric] }} />
            {t('graphStats:series.yesterday')}: <b>{compareTotals.yesterday}</b>{' '}
            {metricUnit}
          </span>
        </div>
      )}
    </section>
  )
}

export const GraphStats = memo(FCGraphStats)
